import {Component, OnDestroy, OnInit, Input} from '@angular/core';

import Map from 'ol/Map.js';
import TileLayer from 'ol/layer/Tile.js';
import View from 'ol/View.js';
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import {GeoJSON} from "ol/format";
import {Fill, Stroke, Style, Icon} from "ol/style";
import {Extent, extend} from 'ol/extent';
import {ProjectionLike} from "ol/proj";
import {Geometry} from "ol/geom";
import SimpleGeometry from 'ol/geom/SimpleGeometry';
import proj4 from "proj4";
import {projections, wmsLayers} from "../../constants";
import {PlotsService} from "../../services/plots.service";
import {Polygon, Point} from "geojson";
import {Feature} from "ol";
import {register} from "ol/proj/proj4";
import {Layer} from "ol/layer";
import {Subscription} from "rxjs";
import {TileWMS} from "ol/source";
import {selectedPhotoChange, selectedPlotsChange, 
  stateSet, referencePointTriggered, referencePointSet, referencePointExists,
  referenceMarkerParams, referenceMarker, SelectedPhotoChangeParams} from '../../events';
import {FeatureLike} from "ol/Feature";
import {defaults as defaultControls} from 'ol/control/defaults.js';
import {ReferencePointControl} from './point-control.component';
import {FeatherModule} from "angular-feather";

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    imports: [FeatherModule]
})



export class MapComponent implements OnInit, OnDestroy {
  @Input() userIsCustomer: boolean = false;
  map: Map | undefined;
  selectedPlotIds: number[] = [];

  baseLayer: TileLayer<TileWMS> | undefined;
  stateExtent: Extent | undefined;
  plotLayer: Layer | undefined;
  locationLayer: Layer | undefined;
  markerLayer: Layer | undefined;
  polygonExtent: Extent | undefined;
  locationExtent: Extent | undefined;

  plotChangeSubscription: Subscription | undefined;
  getPlotGeomSubscription: Subscription | undefined;
  photoChangeSubscription: Subscription | undefined;
  stateChangedSubscription: Subscription | undefined;
  referencePointTriggeredSubscription: Subscription | undefined;
  referencePointExistsSubscription: Subscription | undefined;

  referencePointActive: boolean = false;
  referenceMarkerType: 'nbf' | 'usage' | null = null;

  constructor(private plotsService: PlotsService) {
    proj4.defs(projections.utm32n, "+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs");
    register(proj4);
  }
  

  ngOnInit() {
    const tileLayers: TileLayer<TileWMS>[] = [];

    // wmsLayers.forEach(wmsInfo => {
    //   tileLayers.push(new TileLayer({
    //     source: new TileWMS({
    //       url: wmsInfo.baseUrl,
    //       params: {'LAYERS': wmsInfo.layer, 'TILES': true},
    //       attributions: wmsInfo.attribution + ` (<a href="${wmsInfo.licenceUrl}" target="_blank">${wmsInfo.licence}</a>)`
    //     })
    //   }));
    // });

    this.map = new Map({
      target: 'olMap',
      controls: defaultControls({
        attributionOptions: {
          collapsible: true
        }
      }),
      view: new View({
        projection: projections.webMercator,
        center: [1113195, 6710219],
        zoom: 4
      }),
    });

    this.map.addControl(new ReferencePointControl(this.map))

    this.stateChangedSubscription = stateSet.subscribe(state => {
      const layerInfo = wmsLayers.find(l => l.state === state.id);
      if (layerInfo) {
        if (this.baseLayer) {
          this.map?.removeLayer(this.baseLayer);
        }
        this.baseLayer = new TileLayer({
          source: new TileWMS({
            url: layerInfo.baseUrl,
            params: {'LAYERS': layerInfo.layer, 'TILES': true},
            attributions: layerInfo.attribution + ` (<a href="${layerInfo.licenceUrl}" target="_blank">${layerInfo.licence}</a>)`
          })
        });
        this.stateExtent = state.extent
        this.map?.addLayer(this.baseLayer);
        this.map?.getView().fit(this.stateExtent);
      }
    });

    this.plotChangeSubscription = selectedPlotsChange.subscribe(data => {
      this.locationExtent = undefined;
      this.removePlot();
      this.removeReferenceMarker();
      this.removePhotoLocation();
      this.referenceMarkerType = null;
      this.referencePointActive = false;

      if (data.plotIds.length >= 1) {
        this.selectedPlotIds = data.plotIds;
        this.getPlotGeomSubscription = this.plotsService.getPlotGeom(this.selectedPlotIds[0], this.userIsCustomer).subscribe({
          next: geom => {
            this.addPlot(geom);
            this.getPlotGeomSubscription?.unsubscribe();
          },
          error: (e) => {
            console.error(e);
            // showNotification.next({title: 'Error', message: `2 No plot found for ID ${data.plotIds[0]}.`});
          }
        });
      } else {
        if (this.stateExtent) {
          this.map?.getView().fit(this.stateExtent);
        }
        
        this.getPlotGeomSubscription?.unsubscribe();
      }
    });

    this.photoChangeSubscription = selectedPhotoChange.subscribe((photoData: SelectedPhotoChangeParams | null ) => {
      console.log('photo change subscription', photoData);
      this.removePhotoLocation();

      if (photoData) {
        const geom: Point = this.createPoint(photoData);
        this.addLocation(geom, photoData.angle, photoData.detail, photoData.source);
      }
    });

    this.referencePointExistsSubscription = referencePointExists.subscribe(referenceMarker => {
      // console.log('*** received reference point', referenceMarker);
      this.referencePointActive = false;
      this.referenceMarkerType = null;
      this.removeReferenceMarker();
      this.referenceMarkerType = referenceMarker.nbf ? 'nbf' : 'usage';
      this.addReferenceMarker(referenceMarker, true);
    });

    const me = this;

    this.map.on('singleclick', function(evt) {
      if (me.referencePointActive) {
        me.referencePointActive = false;
        const coord = evt.coordinate;
        console.log('clicked on map at', coord);
        
        const hit = me.checkHoverOverPlot(evt);
        if (hit) {
          // console.log('create point 1', coord);
          const point = me.createPoint({geom: {
            lat: coord[1],
            lon: coord[0]}
          });
          // console.log('created point', point);
          
          // if (reprojected && reprojected instanceof SimpleGeometry) {
            // console.log('create point 2', reprojected.getCoordinates());
            // const reprojectedCoords = reprojected.getCoordinates();
            // if (!reprojectedCoords) {
              // return;
            // }
            // const newPoint = me.createPoint({geom: {
              // lat: coord[1],
              // lon: coord[0]}
            // });
            // console.log('created point', newPoint);
            const referencePoint = {marker: point, nbf: me.referenceMarkerType === 'nbf'};
            me.addReferenceMarker(referencePoint)

            const reprojected = me.reprojectGeom(point, projections.webMercator, projections.utm32n);
            if (reprojected && reprojected instanceof SimpleGeometry) {
              const coordinates = reprojected.getCoordinates();
              console.log('got coordinates', coordinates);
              if (coordinates) {
                referencePointSet.next({marker: {
                  type: 'Point', coordinates: coordinates
                }, nbf:me.referenceMarkerType === 'nbf'});
              }
           }
          // }
        }        
      }
    });

    this.map.on('pointermove', function(evt) {
      if (evt.dragging) {
        evt.target.targetElement_.style.cursor = '-webkit-grabbing';
        return;
      };

      const hit = me.checkHoverOverPlot(evt);
      if (me.referencePointActive && hit) { 
        evt.target.targetElement_.style.cursor = 'crosshair';
      } else {
        evt.target.targetElement_.style.cursor = '-webkit-grab';
      }
    });

    this.referencePointTriggeredSubscription = referencePointTriggered.subscribe((params: referenceMarkerParams) => {
      // console.log('reference point triggered 1', this.markerLayer, params);
      this.removeReferenceMarker();
      if (params.checked) {
        this.referencePointActive = true;
        // console.log('reference point triggered 2', this.markerLayer, params);
        if (params.nbf !== undefined) {
          this.referenceMarkerType = params.nbf ? 'nbf' : 'usage';
        } // else keep the current value
      } else {
        this.referenceMarkerType = null;
      }
    });
  }

  private checkHoverOverPlot(evt: any) {
    const hit = this.map?.forEachFeatureAtPixel(evt.pixel, function(feature, layer) {
      return feature;
    });
    return !!hit?.getGeometry();
  }

  createPoint(location: any): Point {
    return {
      type: 'Point',
      coordinates: [location.geom.lon, location.geom.lat]
    }
  }

  ngOnDestroy() {
    this.plotChangeSubscription?.unsubscribe();
    this.getPlotGeomSubscription?.unsubscribe();
    this.photoChangeSubscription?.unsubscribe();
    this.stateChangedSubscription?.unsubscribe();
    this.referencePointTriggeredSubscription?.unsubscribe();
    this.referencePointExistsSubscription?.unsubscribe();
  }

  private addPlot(geom: Polygon) {
    const feature = new Feature(this.reprojectGeom(geom, projections.utm32n, projections.webMercator))

    const source = new VectorSource({features: [feature]});
    const style = new Style({
      stroke: new Stroke({
        color: 'rgba(255, 255, 0, 0.8)',
        lineDash: [4],
        width: 2,
      }),
      fill: new Fill({
        color: 'rgba(255, 255, 0, 0.1)',
      }),
    });
    this.plotLayer = new VectorLayer({source, zIndex: 5, style});
    this.map?.addLayer(this.plotLayer);
    const extent = source.getExtent();
    this.polygonExtent = JSON.parse(JSON.stringify(extent));
    if (this.polygonExtent) {
      if (this.locationExtent && extent) {
        this.map?.getView().fit(extend(extent, this.locationExtent), {padding: [35, 35, 35, 35]});
      } else {
        this.map?.getView().fit(this.polygonExtent, {padding: [25, 25, 25, 25]});
      }
    }
  }

  private async addReferenceMarker(referenceMarker: referenceMarker, reproject: boolean = false) {
    // console.log('will add reference marker to map, reproject', referenceMarker, reproject);
    let feature;
    if (reproject) {
      feature = new Feature(this.reprojectGeom(referenceMarker.marker, projections.utm32n, projections.webMercator))
    } else {
      feature = new Feature(this.reprojectGeom(referenceMarker.marker, projections.webMercator, projections.webMercator))
    }
    
    const source = new VectorSource({features: [feature]});
    const iconPath = referenceMarker.nbf ? 'assets/img/dark-red-marker-icon.png' : 'assets/img/orange-marker-icon.png';
    const anchor = [0.5, 41];
    const icon = new Icon({
      anchor: anchor,
      anchorXUnits: 'fraction',
      anchorYUnits: 'pixels',
      src: iconPath,
    });
    const style = new Style({
      image: icon
    });
    this.markerLayer = new VectorLayer({source, zIndex: 6, style});
    this.map?.addLayer(this.markerLayer);

    //  // center map on both features
    //  const extent = source.getExtent()
    //  this.locationExtent = JSON.parse(JSON.stringify(extent));
    //  if (extent && this.polygonExtent) {
    //    this.map?.getView().fit(extend(extent, this.polygonExtent), {padding: [35, 35, 35, 35]});
    //  }
  }

  private async addLocation(geom: Point, angle: number, detail: boolean, dataSource: string) {
    console.log('would add location', geom, angle);
    const directionMarker = (angle != null && angle !== -1 && !detail);
    const feature = new Feature(this.reprojectGeom(geom, projections.wgs84, projections.webMercator))
    const source = new VectorSource({features: [feature]});
    const rotation = directionMarker ? angle * (Math.PI / 180) : 0;

    const markerType = dataSource.toLowerCase().indexOf('sfb') !== -1 ? 'sfb' : 'ast';

    const iconPath = directionMarker ? 'assets/img/icons8-navigate-36-' + markerType + '.png' : 'assets/img/' + markerType + '-marker-icon.png';
    const anchor = directionMarker ? undefined : [0.5, 41];
    const icon = new Icon({
      anchor: anchor,
      anchorXUnits: 'fraction',
      anchorYUnits: 'pixels',
      src: iconPath,
    });

    icon.setRotation(rotation)
    const style = new Style({
      image: icon
    });

    this.locationLayer = new VectorLayer({source, zIndex: 6, style});
    this.map?.addLayer(this.locationLayer);

    // center map on both features
    const extent = source.getExtent()
    this.locationExtent = JSON.parse(JSON.stringify(extent));
    if (extent && this.polygonExtent) {
      this.map?.getView().fit(extend(extent, this.polygonExtent), {padding: [35, 35, 35, 35]});
    }
  }

  private reprojectGeom(geom: any, from: ProjectionLike, to: ProjectionLike): Geometry | undefined {
    let geometry: Geometry | undefined;
    const feature: FeatureLike = new GeoJSON().readFeature({
      type: 'Feature',
      geometry: geom,
      properties: {}
    }) as FeatureLike;
    // @ts-ignore
    geometry = feature.getGeometry();
    if (geometry) {
      const transformed = geometry.clone().transform(from, to);
      console.log('transformed geom', transformed);
      return geometry.clone().transform(from, to);
    } else {
      return undefined
    }
  }

  private removePlot() {
    if (this.plotLayer) {
      this.map?.removeLayer(this.plotLayer);
    }
  }

  private removeReferenceMarker() {
    if (this.markerLayer) {
      this.map?.removeLayer(this.markerLayer);
      this.markerLayer = undefined;
    }
  }

  private removePhotoLocation() {
    if (this.locationLayer) {
      this.map?.removeLayer(this.locationLayer);
    }
  }
}
